import type {
  ApiErrorBody,
  ApiErrorCode,
  ApiFieldIssue,
  ApiPaginated,
  PaginationMeta,
} from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000/api/v1';

/**
 * Typed representation of a backend failure. Carries the machine-readable code
 * so callers can branch on `BOOK_NOT_AVAILABLE` instead of matching strings, and
 * the per-field issues so forms can bind errors to inputs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: ApiFieldIssue[];

  constructor(status: number, code: ApiErrorCode, message: string, details: ApiFieldIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field errors keyed by the input name, e.g. `{ weeks: '...' }`. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const issue of this.details) {
      // Paths arrive as "body.weeks" / "query.page"; forms only know "weeks".
      const field = issue.path.replace(/^(body|query|params)\./, '');
      result[field] ??= issue.message;
    }
    return result;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Could not reach the server. Check your connection and try again.') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ---------------------------------------------------------------------------
// Access token handling
// ---------------------------------------------------------------------------

/**
 * The access token is held in memory only.
 *
 * localStorage would make it readable by any injected script; the long-lived
 * refresh token lives in an httpOnly cookie instead, and a page reload silently
 * re-establishes the session through /auth/refresh. That is the standard
 * trade-off for SPAs and it means an XSS bug cannot walk away with a token.
 */
let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

/** Registered by the auth store so a dead session can clear app state once. */
export const setSessionExpiredHandler = (handler: (() => void) | null): void => {
  onSessionExpired = handler;
};

// ---------------------------------------------------------------------------
// Refresh coordination
// ---------------------------------------------------------------------------

/**
 * A single in-flight refresh shared by every caller.
 *
 * Without this, a page that fires five queries at once would send five refresh
 * requests; since refresh tokens rotate and reuse is treated as theft, four of
 * them would be rejected and the whole token family revoked. Funnelling through
 * one promise keeps rotation safe.
 */
let refreshInFlight: Promise<string | null> | null = null;

const requestRefresh = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Sends the httpOnly refresh cookie.
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (!response.ok) return null;

    const body = (await response.json()) as { data?: { accessToken?: string } };
    return body.data?.accessToken ?? null;
  } catch {
    return null;
  }
};

const refreshAccessToken = (): Promise<string | null> => {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | undefined | null;

/**
 * Builds a request URL from the base, a path and an optional query.
 *
 * Deliberately string concatenation plus `URLSearchParams` rather than `new URL`:
 * `API_BASE_URL` is the relative `/api/v1` in single-origin mode, and `new URL`
 * throws on a relative URL unless it is handed a base. `URLSearchParams` has no
 * such requirement, so this works for a relative base and an absolute one
 * without either the caller or this function knowing which it got.
 */
const buildUrl = (path: string, query?: Record<string, QueryValue>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    // Skipping empties keeps URLs clean and lets the server apply defaults.
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return `${API_BASE_URL}${path}${search ? `?${search}` : ''}`;
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Internal: prevents an infinite refresh loop. */
  isRetry?: boolean;
  signal?: AbortSignal;
}

const parseError = async (response: Response): Promise<ApiError> => {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = undefined;
  }

  return new ApiError(
    response.status,
    body?.error.code ?? 'INTERNAL_ERROR',
    body?.error.message ?? `Request failed with status ${response.status}.`,
    body?.error.details ?? [],
  );
};

const rawRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { method = 'GET', query, body, isRetry = false, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    // Let cancellation propagate so TanStack Query can distinguish it.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError();
  }

  if (response.status === 401 && !isRetry) {
    // Access tokens are short lived; transparently refresh once and replay.
    const nextToken = await refreshAccessToken();
    if (nextToken) {
      setAccessToken(nextToken);
      return rawRequest<T>(path, { ...options, isRetry: true });
    }
    setAccessToken(null);
    onSessionExpired?.();
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  // 204 No Content, e.g. DELETE.
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
};

/** Unwraps `{ data }` and returns the payload. */
const unwrap = async <T>(path: string, options?: RequestOptions): Promise<T> => {
  const body = await rawRequest<{ data: T }>(path, options);
  return body.data;
};

/** Unwraps `{ data, meta }` for list endpoints. */
const unwrapPaginated = async <T>(
  path: string,
  options?: RequestOptions,
): Promise<{ items: T[]; pagination: PaginationMeta }> => {
  const body = await rawRequest<ApiPaginated<T>>(path, options);
  return { items: body.data, pagination: body.meta.pagination };
};

/**
 * Sends a File as the raw request body, using the browser-reported MIME type.
 *
 * Kept separate from `rawRequest` because that helper JSON-stringifies its body.
 * Deliberately not FormData: the upload endpoints carry a single file and no
 * other fields, so multipart would add framing for no benefit.
 */
export const uploadRaw = async <T>(path: string, file: File): Promise<T> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // The server re-verifies the format from the bytes, so this is a hint only.
    'Content-Type': file.type || 'application/octet-stream',
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: file,
    });
  } catch {
    throw new NetworkError();
  }

  if (response.status === 401) {
    const nextToken = await refreshAccessToken();
    if (nextToken) {
      setAccessToken(nextToken);
      return uploadRaw<T>(path, file);
    }
    setAccessToken(null);
    onSessionExpired?.();
  }

  if (!response.ok) throw await parseError(response);

  const body = (await response.json()) as { data: T };
  return body.data;
};

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    unwrap<T>(path, { method: 'GET', query, ...(signal ? { signal } : {}) }),

  list: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    unwrapPaginated<T>(path, { method: 'GET', query, ...(signal ? { signal } : {}) }),

  post: <T>(path: string, body?: unknown) => unwrap<T>(path, { method: 'POST', body }),

  patch: <T>(path: string, body?: unknown) => unwrap<T>(path, { method: 'PATCH', body }),

  delete: (path: string) => rawRequest<void>(path, { method: 'DELETE' }),

  /** Bypasses the `{ data }` unwrap, for endpoints that may return 202. */
  raw: rawRequest,
};

export { API_BASE_URL, refreshAccessToken };
